import { api } from "@/lib/api";

// Lumina Documents — document intake, OCR/classification/extraction and patient
// matching, all scoped to the currently selected 4-digit client_id.
// Upstream base is https://ehi.ariaone.ai, reached through our /api/proxy.
const B = "/api/edi/documents";

// One call per file. body: { client_id, filename, content_type, base64_data (no data: prefix) }
// → 202 { job_id, status:"processing", poll_url }
export async function uploadDocument({ client_id, filename, content_type, base64_data }) {
  const { data } = await api.post(`${B}/upload`, { client_id, filename, content_type, base64_data });
  return data;
}

// Poll every 2s. processing → { progress.stage }, completed → { analysis_id, result, edi_client_match },
// failed → { error }.
export async function getJob(jobId) {
  const { data } = await api.get(`${B}/jobs/${jobId}`);
  return data;
}

// client_id required. Optional filters: link_status (auto_linked|needs_review|confirmed), doc_type.
export async function listAnalyses({ client_id, skip = 0, limit = 30, link_status, doc_type }) {
  const params = { client_id, skip, limit };
  if (link_status) params.link_status = link_status;
  if (doc_type) params.doc_type = doc_type;
  const { data } = await api.get(`${B}/analyses`, { params });
  return data;
}

export async function getAnalysis(analysisId) {
  const { data } = await api.get(`${B}/analyses/${analysisId}`);
  return data;
}

// → { filename, content_type, file_content (base64) }
export async function getFileContent(documentId) {
  const { data } = await api.get(`${B}/files/${documentId}/content`);
  return data;
}

// Confirm / change the patient. → link status becomes "confirmed" (confidence 1.0).
export async function assignPatient(analysisId, patient) {
  const { data } = await api.post(`${B}/analyses/${analysisId}/assign`, patient);
  return data;
}

// Patient search is per client. Live chart DB first, then roster, then imported.
export async function searchPatients({ client_id, q }) {
  const { data } = await api.get(`/api/edi/note-to-claim/pg-patient-search`, { params: { client_id, q } });
  return data;
}

// Read a File into base64 without the `data:...;base64,` prefix.
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result || ""); resolve(s.slice(s.indexOf(",") + 1)); };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/tiff", "image/heic"];

// ocr → Scanning, classifying → Classifying, extracting → Extracting, finalizing → Finalizing
export const STAGE_STEPS = [
  { key: "ocr", label: "Scanning" },
  { key: "classifying", label: "Classifying" },
  { key: "extracting", label: "Extracting" },
  { key: "finalizing", label: "Finalizing" },
];

export function engineTag(engine) {
  const e = String(engine || "").toLowerCase();
  if (e === "astra") return "A";
  if (e === "gpt4" || e === "gpt-4") return "4";
  return (e[0] || "·").toUpperCase();
}

export function genderCode(g) {
  const s = String(g || "").trim().toUpperCase();
  if (s.startsWith("F")) return "F";
  if (s.startsWith("M")) return "M";
  return s ? s[0] : "";
}

// Pull a few of the most useful extracted fields for an at-a-glance row summary.
const SUMMARY_META = new Set([
  "extraction_warnings", "extraction_failed", "error_reason", "raw_text", "raw_text_full",
  "confidence", "notes", "summary", "indicator_profile", "should_process", "relevance",
]);
const SUMMARY_PREF = [
  ["patient_name", "Patient"], ["patient_full_name", "Patient"], ["full_name", "Patient"],
  ["date_of_birth", "DOB"], ["dob", "DOB"],
  ["encounter_date", "Date"], ["date_of_service", "DOS"], ["service_date", "DOS"],
  ["visit_date", "Date"], ["document_date", "Date"], ["date", "Date"],
  ["provider_name", "Provider"], ["rendering_provider", "Provider"], ["ordering_provider", "Provider"],
  ["encounter_type", "Type"], ["chief_complaint", "Chief complaint"],
  ["mrn", "MRN"], ["account_number", "Acct"],
  ["diagnoses", "Dx"], ["icd_codes", "Dx"], ["cpt_codes", "CPT"], ["procedures", "Procedures"],
  ["payer_name", "Payer"], ["payer", "Payer"], ["insurance", "Insurance"], ["member_id", "Member"],
  ["total", "Total"], ["total_amount", "Total"], ["amount", "Amount"], ["balance", "Balance"],
];

function flattenLeaves(obj, out = {}) {
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    if (SUMMARY_META.has(k) || v == null || v === "") continue;
    if (Array.isArray(v)) {
      const items = v.map((x) => (x && typeof x === "object" ? (x.code || x.name || x.description || x.value || "") : x)).filter((s) => s !== "" && s != null);
      if (items.length && out[k] == null) out[k] = items.slice(0, 3).join(", ");
    } else if (typeof v === "object") {
      flattenLeaves(v, out);
    } else if (out[k] == null) {
      out[k] = v;
    }
  }
  return out;
}

const prettyLabel = (k) => String(k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function summarizeExtraction(extraction, max = 4) {
  if (!extraction || extraction.extraction_failed) return [];
  const leaves = flattenLeaves(extraction);
  const used = new Set();
  const res = [];
  for (const [key, label] of SUMMARY_PREF) {
    if (leaves[key] != null && !used.has(key)) {
      res.push({ label, value: String(leaves[key]) });
      used.add(key);
      if (res.length >= max) return res;
    }
  }
  for (const [k, v] of Object.entries(leaves)) {
    if (used.has(k)) continue;
    res.push({ label: prettyLabel(k), value: String(v) });
    used.add(k);
    if (res.length >= max) break;
  }
  return res;
}
